import { useState } from "react";
import {
  useNotificationLogs,
  useNotificationStats,
  type NotificationChannel,
  type NotificationEventType,
  type NotificationStatus,
  type NotificationLog,
} from "@/api/notification-logs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Mail,
  MessageSquare,
  Bell,
  CheckCircle,
  XCircle,
  Clock,
  SkipForward,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";

const statusConfig: Record<
  NotificationStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: typeof CheckCircle;
  }
> = {
  SENT: { label: "Sent", variant: "default", icon: CheckCircle },
  FAILED: { label: "Failed", variant: "destructive", icon: XCircle },
  PENDING: { label: "Pending", variant: "outline", icon: Clock },
  SKIPPED: { label: "Skipped", variant: "secondary", icon: SkipForward },
};

const channelIcon: Record<NotificationChannel, typeof Mail> = {
  EMAIL: Mail,
  SMS: MessageSquare,
  PUSH: Bell,
};

const eventTypeLabels: Record<NotificationEventType, string> = {
  ARRIVING_SOON: "Arriving Soon",
  OUT_FOR_PICKUP: "Out for Pickup",
  PICKED_UP: "Picked Up",
  PICKUP_FAILED: "Pickup Failed",
  OUT_FOR_DELIVERY: "Out for Delivery",
  DELIVERED: "Delivered",
  DELIVERY_FAILED: "Delivery Failed",
  OUT_FOR_RETURN: "Out for Return",
  RETURNED: "Returned",
  RETURN_FAILED: "Return Failed",
  IN_TRANSIT: "In Transit",
  HANDED_TO_CARRIER: "Handed to Carrier",
  DISPATCHED: "Dispatched",
  ORDER_CONFIRMED: "Order Confirmed",
  LABEL_READY: "Label Ready",
};

export default function NotificationLogsScreen() {
  const [page, setPage] = useState(1);
  const [shipmentNumber, setShipmentNumber] = useState("");
  const [channel, setChannel] = useState<NotificationChannel | "">("");
  const [status, setStatus] = useState<NotificationStatus | "">("");
  const [eventType, setEventType] = useState<NotificationEventType | "">("");

  const { data: logsData, isLoading } = useNotificationLogs({
    page,
    limit: 20,
    shipmentNumber: shipmentNumber ? Number(shipmentNumber) : undefined,
    channel: channel || undefined,
    status: status || undefined,
    eventType: eventType || undefined,
  });

  const { data: stats } = useNotificationStats();

  const handleClearFilters = () => {
    setShipmentNumber("");
    setChannel("");
    setStatus("");
    setEventType("");
    setPage(1);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Notification Logs</h1>
        <p className="text-muted-foreground">
          View SMS, email, and push notifications sent to customers
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Last 24h
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.recent24h}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                SMS Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.byChannel.SMS || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Emails Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.byChannel.EMAIL || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Failed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                {stats.byStatus.FAILED || 0}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-end flex-wrap">
            <div className="w-[140px]">
              <label className="text-sm font-medium mb-1.5 block">
                Shipment #
              </label>
              <Input
                type="number"
                placeholder="e.g. 1234"
                value={shipmentNumber}
                onChange={(e) => {
                  setShipmentNumber(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="w-[140px]">
              <label className="text-sm font-medium mb-1.5 block">
                Channel
              </label>
              <Select
                value={channel || "all"}
                onValueChange={(v) => {
                  setChannel(v === "all" ? "" : (v as NotificationChannel));
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="SMS">SMS</SelectItem>
                  <SelectItem value="EMAIL">Email</SelectItem>
                  <SelectItem value="PUSH">Push</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[140px]">
              <label className="text-sm font-medium mb-1.5 block">Status</label>
              <Select
                value={status || "all"}
                onValueChange={(v) => {
                  setStatus(v === "all" ? "" : (v as NotificationStatus));
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="SENT">Sent</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="SKIPPED">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <label className="text-sm font-medium mb-1.5 block">
                Event Type
              </label>
              <Select
                value={eventType || "all"}
                onValueChange={(v) => {
                  setEventType(v === "all" ? "" : (v as NotificationEventType));
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {Object.entries(eventTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={handleClearFilters}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : logsData?.data.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No notification logs found
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Channel</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Shipment</TableHead>
                    <TableHead>Message Preview</TableHead>
                    <TableHead className="w-[160px]">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsData?.data.map((log: NotificationLog) => {
                    const statusCfg = statusConfig[log.status];
                    const StatusIcon = statusCfg.icon;
                    const ChannelIcon = channelIcon[log.channel];
                    return (
                      <TableRow key={log.id}>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <ChannelIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{log.channel}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {eventTypeLabels[log.eventType] || log.eventType}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">
                              {log.recipientType}
                            </span>
                            <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {log.recipientValue}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusCfg.variant} className="gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {statusCfg.label}
                          </Badge>
                          {log.errorMessage && (
                            <p className="text-xs text-destructive mt-1 max-w-[150px] truncate">
                              {log.errorMessage}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium">
                            {log.shipment?.shipmentNumber
                              ? `SHP-${log.shipment.shipmentNumber}`
                              : "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground line-clamp-2 max-w-[200px]">
                            {log.messagePreview || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(log.createdAt), "MMM d, h:mm a")}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {logsData && logsData.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-sm text-muted-foreground">
                    Page {logsData.pagination.page} of{" "}
                    {logsData.pagination.totalPages} (
                    {logsData.pagination.total} total)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= logsData.pagination.totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
